import { useMemo } from 'react';
import type { Effect, EffectType } from '@cinegrid/core';
import { useEditorStore } from '../../state/editorStore';
import { PanelShell, SectionLabel, SliderRow } from '../controls/PanelShell';

const EFFECT_TYPES: EffectType[] = ['blur', 'sharpen', 'vignette', 'glow', 'noise'];

export function EffectsPanel() {
  const selection = useEditorStore((s) => s.selection);
  const project = useEditorStore((s) => s.project);
  const updateClipProperties = useEditorStore((s) => s.updateClipProperties);
  const addCrossDissolveToSelected = useEditorStore((s) => s.addCrossDissolveToSelected);

  const clip = useMemo(
    () => project.clips.find((c) => c.id === selection[0]),
    [project.clips, selection]
  );

  if (!clip) {
    return (
      <PanelShell title="Effects">
        <p className="p-3 text-xs text-text-low">Select a clip to add effects.</p>
      </PanelShell>
    );
  }

  const effects = clip.properties.effects;

  const addEffect = (type: EffectType) => {
    const fx: Effect = {
      id: crypto.randomUUID(),
      type,
      enabled: true,
      amount: 0.5,
      params: {},
    };
    updateClipProperties(clip.id, { effects: [...effects, fx] });
  };

  return (
    <PanelShell title="Effects">
      <SectionLabel>Transitions</SectionLabel>
      <div className="p-2">
        <button
          type="button"
          onClick={addCrossDissolveToSelected}
          className="border border-hairline bg-raised px-2 py-1.5 text-2xs text-text-high hover:border-accent hover:text-accent rounded-lg"
        >
          Add Cross Dissolve (0.5s)
        </button>
        {clip.transitionOut && (
          <p className="mt-2 text-2xs text-text-low">
            Out: {clip.transitionOut.type} · {clip.transitionOut.duration}s
          </p>
        )}
      </div>
      <SectionLabel>Add Effect</SectionLabel>
      <div className="flex flex-wrap gap-1 p-2">
        {EFFECT_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => addEffect(t)}
            className="rounded-lg border border-hairline bg-raised px-2 py-1 text-2xs capitalize text-text-low hover:text-accent"
          >
            {t}
          </button>
        ))}
      </div>
      <SectionLabel>Stack</SectionLabel>
      {effects.length === 0 && <p className="p-3 text-2xs text-text-low">No effects.</p>}
      {effects.map((fx) => (
        <div key={fx.id} className="border-b border-hairline py-1">
          <div className="flex items-center justify-between px-2">
            <label className="flex items-center gap-1.5 text-2xs capitalize">
              <input
                type="checkbox"
                checked={fx.enabled}
                onChange={(e) =>
                  updateClipProperties(clip.id, {
                    effects: effects.map((f) =>
                      f.id === fx.id ? { ...f, enabled: e.target.checked } : f
                    ),
                  })
                }
              />
              {fx.type}
            </label>
            <button
              type="button"
              className="text-2xs text-text-low hover:text-danger"
              onClick={() =>
                updateClipProperties(clip.id, {
                  effects: effects.filter((f) => f.id !== fx.id),
                })
              }
            >
              Remove
            </button>
          </div>
          <SliderRow
            label="Amount"
            value={fx.amount}
            min={0}
            max={1}
            onChange={(amount) =>
              updateClipProperties(clip.id, {
                effects: effects.map((f) => (f.id === fx.id ? { ...f, amount } : f)),
              })
            }
          />
        </div>
      ))}
    </PanelShell>
  );
}
